import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertTriangle, CalendarPlus, Home, Info } from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { ErrorDeApi } from '@/shared/api/problema';
import type { Cliente, PreviaDeAnticipacion } from '@/shared/api/tipos';
import { CampoDelPanel, PanelLateral } from '@/shared/ui/PanelLateral';
import { SelectorDeMes, mesDe } from '@/shared/ui/SelectorDeMes';
import { notificar } from '@/shared/ui/avisos';

export function PanelDeAnticipacion({
  cliente,
  abierto,
  alCerrar,
  alGenerar,
}: {
  cliente: Cliente;
  abierto: boolean;
  alCerrar: () => void;
  alGenerar: () => void;
}) {
  const [hasta, setHasta] = useState(mesDe());
  const [anio, setAnio] = useState(new Date().getFullYear());
  const [error, setError] = useState<string | null>(null);

  /**
   * Los predios que NO se adelantan.
   *
   * El caso que pidió EPAGAL: el propietario alquila y cada inquilino adelanta lo suyo. Se
   * guarda lo quitado porque adelantarlos todos es lo normal y quitar uno es la excepción.
   */
  const [fuera, setFuera] = useState<Set<number>>(new Set());

  const quitados = [...fuera].sort((a, b) => a - b);

  function alternar(cuentaId: number) {
    const siguiente = new Set(fuera);
    if (siguiente.has(cuentaId)) {
      siguiente.delete(cuentaId);
    } else {
      siguiente.add(cuentaId);
    }
    setFuera(siguiente);
  }

  const previa = useQuery({
    // Lo quitado entra en la clave: al desmarcar un predio hay que volver a preguntar, porque
    // el mes de arranque puede ser otro si ese predio iba mas adelantado que los demas.
    queryKey: ['anticipacion-previa', cliente.id, hasta, quitados.join(',')],
    queryFn: () =>
      api.get<PreviaDeAnticipacion>(`/emisiones/anticipar/${cliente.id}/${hasta}`, {
        query: quitados.length > 0 ? { sinEstos: quitados.join(',') } : {},
      }),
    enabled: abierto && /^\d{4}-\d{2}$/.test(hasta),
  });

  const generar = useMutation({
    mutationFn: () =>
      api.post(
        `/emisiones/anticipar/${cliente.id}/${hasta}` +
          (quitados.length > 0 ? `?sinEstos=${quitados.join(',')}` : ''),
        {},
      ),
    onSuccess: () => {
      const cuantos = previa.data?.porGenerar.length ?? 0;
      notificar.exito(
        `${cuantos} ${cuantos === 1 ? 'mes generado' : 'meses generados'} para ${cliente.nombre}`,
      );
      alGenerar();
      alCerrar();
    },
    onError: (causa) => {
      const mensaje =
        causa instanceof ErrorDeApi ? causa.message : 'No se pudieron generar los meses';
      setError(mensaje);
      notificar.error(mensaje);
    },
  });

  const p = previa.data;
  const listo = p !== undefined && p.porGenerar.length > 0 && !p.pasaDelTope;

  return (
    <PanelLateral
      abierto={abierto}
      alCerrar={alCerrar}
      titulo="Adelantar meses"
      descripcion={cliente.nombre}
      icono={CalendarPlus}
      alEnviar={(evento) => {
        evento.preventDefault();
        if (listo) {
          setError(null);
          generar.mutate();
        }
      }}
      enviando={!listo || generar.isPending}
    >
      <p className="flex gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3 text-justify text-[11px] uppercase leading-relaxed text-blue-800">
        <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden />
        <span>
          Se generan las facturas de los meses que aún no se han emitido, para que se puedan pagar
          hoy. <span className="font-semibold">Con la tarifa de hoy</span>: si el salario básico
          sube, la diferencia ya no se recoge en estas facturas.
        </span>
      </p>

      <CampoDelPanel
        etiqueta="Hasta qué mes"
        requerido
        ayuda="Se generarán todos los meses que falten hasta ese, ambos incluidos."
      >
        <SelectorDeMes
          valor={hasta}
          alCambiar={(mes) => {
            setHasta(mes);
            setError(null);
          }}
          anio={anio}
          alCambiarAnio={setAnio}
          minimo={mesDe()}
          marcados={p?.porGenerar ?? []}
          leyenda="se generaría"
          accion="Adelantar hasta"
        />
      </CampoDelPanel>

      {p && (
        <div className="rounded-xl border border-armazon-200 p-4">
          {p.porGenerar.length === 0 ? (
            <p className="text-center text-xs uppercase text-armazon-500">
              Ya está facturado hasta {p.hasta}
            </p>
          ) : (
            <>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-armazon-500">
                Se generarían
              </p>
              <p className="importe mt-1 text-2xl font-bold text-armazon-900">
                ${p.total.toFixed(2)}
              </p>
              <p className="mt-0.5 text-[11px] uppercase text-armazon-500">
                {p.porGenerar.length} {p.porGenerar.length === 1 ? 'factura' : 'facturas'}, de{' '}
                {p.desde} a {p.hasta}
                {' · '}
                <span className="importe">${p.porMes.toFixed(2)}</span> cada mes
              </p>

              {p.predios && p.predios.length > 0 && (
                <div className="mt-3 border-t border-armazon-100 pt-3">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-armazon-500">
                    {p.predios.length === 1
                      ? 'El predio que se factura'
                      : 'Los predios que se facturan, todos en la misma factura'}
                  </p>

                  <ul className="mt-2 space-y-1.5">
                    {p.predios.map((predio) => (
                      <li key={predio.numeroCuenta} className="flex items-start gap-2">
                        {p.predios.length > 1 ? (
                          <input
                            type="checkbox"
                            checked={!fuera.has(predio.cuentaId)}
                            onChange={() => alternar(predio.cuentaId)}
                            aria-label={`Adelantar el predio ${predio.numeroCuenta}`}
                            className="mt-0.5 size-3.5 shrink-0 accent-marca-600"
                          />
                        ) : (
                          <Home className="mt-0.5 size-3.5 shrink-0 text-armazon-400" aria-hidden />
                        )}
                        <span className="min-w-0 flex-1">
                          <span className="importe block text-[11px] font-semibold text-armazon-800">
                            {predio.numeroCuenta}
                          </span>
                          <span className="block truncate text-[10px] uppercase text-armazon-500">
                            {predio.direccion || 'Sin dirección registrada'}
                            {predio.exoneracion && ` · ${predio.exoneracion}`}
                          </span>
                        </span>
                        <span className="importe shrink-0 text-[11px] font-semibold text-armazon-700">
                          ${predio.total.toFixed(2)}
                        </span>
                      </li>
                    ))}
                  </ul>

                  {p.predios.length > 1 && (
                    <p className="mt-2 text-justify text-[10px] uppercase leading-relaxed text-armazon-500">
                      La factura es una por persona, no una por medidor: se adelantan los{' '}
                      {p.predios.length} juntos. No se puede adelantar uno y dejar otro.
                    </p>
                  )}
                </div>
              )}

              {p.pasaDelTope && (
                <p className="mt-3 flex gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-justify text-[11px] uppercase leading-relaxed text-amber-800">
                  <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden />
                  <span>
                    Son más de {p.tope} meses, que es el máximo configurado. Elija un mes más
                    cercano o cambie el tope en Configuración.
                  </span>
                </p>
              )}
            </>
          )}
        </div>
      )}

      {error && (
        <p
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-900"
        >
          {error}
        </p>
      )}
    </PanelLateral>
  );
}
